/**
 * FishipediaView
 * Full collection/bestiary modal showing all fish species,
 * discovery status, personal records, and quality badges.
 */

import { useState, useMemo } from 'react';
import { useGameStore } from '@/state';
import { FISH_DEFINITIONS } from '@/data';
import { FishRarity } from '@/types';
import { FishSpeciesCard } from './FishSpeciesCard';

type SortMode = 'rarity' | 'name' | 'discovered';

const RARITY_ORDER: Record<FishRarity, number> = {
  [FishRarity.COMMON]: 0,
  [FishRarity.UNCOMMON]: 1,
  [FishRarity.RARE]: 2,
  [FishRarity.EPIC]: 3,
  [FishRarity.LEGENDARY]: 4,
};

interface FishipediaViewProps {
  onClose: () => void;
}

export function FishipediaView({ onClose }: FishipediaViewProps) {
  const discoveredFish = useGameStore((s) => s.playerState.discoveredFish);
  const [sortMode, setSortMode] = useState<SortMode>('rarity');
  const [filterDiscovered, setFilterDiscovered] = useState<'all' | 'discovered' | 'undiscovered'>('all');

  const discoveredCount = useMemo(() => {
    const discoveredIds = new Set(discoveredFish.map((d) => d.definitionId));
    return FISH_DEFINITIONS.filter((def) => discoveredIds.has(def.id)).length;
  }, [discoveredFish]);

  const totalCount = FISH_DEFINITIONS.length;
  const progressPercent = totalCount > 0 ? (discoveredCount / totalCount) * 100 : 0;

  const sortedFish = useMemo(() => {
    const discoveredIds = new Set(discoveredFish.map((d) => d.definitionId));

    let filtered = [...FISH_DEFINITIONS];

    // Filter
    if (filterDiscovered === 'discovered') {
      filtered = filtered.filter((def) => discoveredIds.has(def.id));
    } else if (filterDiscovered === 'undiscovered') {
      filtered = filtered.filter((def) => !discoveredIds.has(def.id));
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortMode === 'name') {
        const aName = discoveredIds.has(a.id) ? a.name : 'zzz';
        const bName = discoveredIds.has(b.id) ? b.name : 'zzz';
        return aName.localeCompare(bName);
      }
      if (sortMode === 'discovered') {
        const aDisc = discoveredIds.has(a.id) ? 0 : 1;
        const bDisc = discoveredIds.has(b.id) ? 0 : 1;
        if (aDisc !== bDisc) return aDisc - bDisc;
        return RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity];
      }
      // Default: rarity
      return RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity];
    });

    return filtered;
  }, [discoveredFish, sortMode, filterDiscovered]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-700 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-white">Fishipedia</h2>
            <span className="text-sm text-gray-400">
              {discoveredCount}/{totalCount} discovered
            </span>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-700 rounded-full h-2.5 mb-4">
            <div
              className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2">
            {/* Sort */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Sort:</span>
              {(['rarity', 'name', 'discovered'] as SortMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setSortMode(mode)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    sortMode === mode
                      ? 'bg-blue-600/30 border-blue-500/50 text-blue-300'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {/* Filter */}
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-gray-500">Show:</span>
              {(['all', 'discovered', 'undiscovered'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFilterDiscovered(filter)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    filterDiscovered === filter
                      ? 'bg-blue-600/30 border-blue-500/50 text-blue-300'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Fish list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {sortedFish.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">No fish match your filters.</p>
            </div>
          ) : (
            sortedFish.map((def) => {
              const disc = discoveredFish.find((d) => d.definitionId === def.id) ?? null;
              return <FishSpeciesCard key={def.id} definition={def} discovery={disc} />;
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 shrink-0 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
          >
            Close Fishipedia
          </button>
        </div>
      </div>
    </div>
  );
}
